import React, { useState, useEffect, useRef } from 'react';
import {
    Alert,
    Box,
    Typography,
    Button,
    Link,
    TextField,
    IconButton,
    InputAdornment,
    Container,
    Paper,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import login from '../../assets/login.jpg';
import { AuthService, authManager } from '@df/api';
import { getSafeApiErrorMessage, isNetworkError } from '../../utils/errorUtils';
import { AUTH_BYPASS_DEV, SIGNUP_MODE } from '../../config/featureFlags';
import { getOnboardingBaseUrl } from '../../utils/onboardingBaseUrl';
import {
    getScopedInvitationToken,
    hasScopedInvitationContinuation,
} from '../../utils/scopedInvitationSession';
import {
    VALID_HANDOFFS,
    buildLanternHandoffUrl,
    getHandoffFromLocation,
    getSafeReturnToFromLocation,
} from '../../utils/lanternHandoff';

const shouldResumeScopedInvitation = () =>
    hasScopedInvitationContinuation() && Boolean(getScopedInvitationToken());

export const resolveLoginHandoffTarget = ({ location, lanternBase, accessToken, refreshToken }) => {
    const handoff = getHandoffFromLocation(location);
    if (!VALID_HANDOFFS.has(handoff)) {
        return null;
    }

    return buildLanternHandoffUrl({
        lanternBase,
        handoff,
        accessToken,
        refreshToken,
        returnTo: getSafeReturnToFromLocation(location),
    });
};

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isOpenSignup = SIGNUP_MODE === 'OPEN';
    // Set by the self-service Signup page after the account is created (no auto-login).
    const signupComplete = location.state?.signupComplete === true;
    const signupEmail = signupComplete && typeof location.state?.email === 'string'
        ? location.state.email.trim().toLowerCase()
        : '';
    const [currentTab, setCurrentTab] = useState(0); // 0 for sign in
    const [formData, setFormData] = useState({
        username: signupEmail,
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalStatus, setModalStatus] = useState(null); // 'success' | 'error'
    const [modalMessage, setModalMessage] = useState('');
    const onboardingBaseUrl = getOnboardingBaseUrl();
    const requestAccessUrl = `${onboardingBaseUrl}/request-access`;
    const termsUrl = `${onboardingBaseUrl}/terms`;
    const privacyUrl = `${onboardingBaseUrl}/privacy`;
    const onboardingHomeUrl = `${onboardingBaseUrl}/`;
    const resolveLanternBase = () => {
        const rawBase = process.env.REACT_APP_PUBLIC_LANTERN_V3_BASE || '';
        if (rawBase) {
            return rawBase.replace(/\/+$/, '');
        }
        if (process.env.NODE_ENV === 'production') {
            return null;
        }
        return 'http://localhost:3000';
    };
    const lanternBase = resolveLanternBase();
    
    // One-shot guard for handoff redirect
    const handoffRedirectDone = useRef(false);

    // Reset data when component mounts (keeping the email of a just-created account)
    useEffect(() => {
        setFormData({ username: signupEmail, password: '' });
    }, [signupEmail]);

    // Check if already authenticated on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const logout = urlParams.get('logout');
        if (logout === '1' || logout === 'true') {
            const refreshToken = authManager.getRefreshToken();
            const finalizeLogout = () => {
                authManager.clearTokens();
                urlParams.delete('logout');
                const nextSearch = urlParams.toString();
                const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
                window.history.replaceState({}, '', nextUrl);
            };
            if (refreshToken) {
                AuthService.logout(refreshToken)
                    .catch((error) => {
                        console.warn('[Login] Logout API call failed:', error);
                    })
                    .finally(finalizeLogout);
            } else {
                finalizeLogout();
            }
            return;
        }
        const handoff = getHandoffFromLocation(location);
        const hasProtectedRedirect = Boolean(location.state?.from);
        const hasHandoff = VALID_HANDOFFS.has(handoff);
        if (shouldResumeScopedInvitation() && authManager.isAuthenticated()) {
            handoffRedirectDone.current = true;
            navigate('/scoped-invitation', { replace: true });
            return;
        }
        if (AUTH_BYPASS_DEV && !handoffRedirectDone.current) {
            // Keep /login reachable in bypass mode unless we were redirected here or a handoff is requested.
            if (hasProtectedRedirect || hasHandoff) {
                handoffRedirectDone.current = true;
                if (!lanternBase) {
                    setModalStatus('error');
                    setModalMessage('Lantern base URL is not configured.');
                    setModalOpen(true);
                    return;
                }
                window.location.assign(lanternBase);
            }
            return;
        }
        if (authManager.isAuthenticated() && !handoffRedirectDone.current) {
            if (!lanternBase) {
                setModalStatus('error');
                setModalMessage('Lantern base URL is not configured.');
                setModalOpen(true);
                return;
            }
            // Check for handoff parameter first
            if (hasHandoff) {
                handoffRedirectDone.current = true;
                
                // Get tokens and redirect to Lantern with SSO fragment
                const accessToken = authManager.getAccessToken();
                const refreshToken = authManager.getRefreshToken();
                const targetUrl = resolveLoginHandoffTarget({
                    location,
                    lanternBase,
                    accessToken,
                    refreshToken,
                });

                if (targetUrl) {
                    window.history.replaceState({}, '', window.location.pathname);
                    window.location.assign(targetUrl);
                    return;
                }
            }
            
            // Default behavior: redirect to intended path or portal
            const intendedPath = lanternBase;

            window.location.assign(intendedPath);
        }
    }, [navigate, location.state?.from, location.search]);

    // Handle navigation when authentication state changes
    useEffect(() => {
        if (isAuthenticated && !handoffRedirectDone.current) {
            if (shouldResumeScopedInvitation()) {
                handoffRedirectDone.current = true;
                navigate('/scoped-invitation', { replace: true });
                return;
            }
            if (!lanternBase) {
                setModalStatus('error');
                setModalMessage('Lantern base URL is not configured.');
                setModalOpen(true);
                return;
            }
            // Check for handoff parameter first
            const handoff = getHandoffFromLocation(location);
            const hasHandoff = VALID_HANDOFFS.has(handoff);
            
            if (hasHandoff) {
                handoffRedirectDone.current = true;
                
                // Get tokens and redirect to Lantern with SSO fragment
                const accessToken = authManager.getAccessToken();
                const refreshToken = authManager.getRefreshToken();
                const targetUrl = resolveLoginHandoffTarget({
                    location,
                    lanternBase,
                    accessToken,
                    refreshToken,
                });

                if (targetUrl) {
                    window.history.replaceState({}, '', window.location.pathname);
                    window.location.assign(targetUrl);
                    return;
                }
            }
            
            // Default behavior: redirect to intended path or portal
            const intendedPath = lanternBase;

            window.location.assign(intendedPath);
        }
    }, [isAuthenticated, navigate, location.state?.from, location.search]);



    const handleTogglePassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSignIn = async (event) => {
        event.preventDefault();
        
        if (!formData.username || !formData.password) {
            setModalStatus('error');
            setModalMessage('Please enter both username and password.');
            setModalOpen(true);
            return;
        }

        setLoading(true);
        try {
            const response = await AuthService.login({ username: formData.username, password: formData.password });
            
            // Store tokens in authManager (in-memory)
            if (response.access && response.refresh) {
                authManager.setTokens(response.access, response.refresh);
                
                // Set authenticated state - navigation will be handled by useEffect
                setIsAuthenticated(true);
            } else {
                throw new Error('No access token received');
            }
        } catch (error) {
            console.error('Login error:', error);
            let message = getSafeApiErrorMessage(error, 'Unexpected server response.');
            const status = Number(error?.status);
            if (isNetworkError(error)) {
                message = 'Server unreachable. Is the backend running?';
            } else if (status === 401 || status === 400) {
                message = 'Invalid email or password.';
            }
            setModalStatus('error');
            setModalMessage(message);
            setModalOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        navigate('/forgot-password');
    };

    const handleReturnToHome = () => {
        window.location.assign(onboardingHomeUrl);
    };

    const renderSignInForm = () => (
        <Box sx={{ width: '100%', maxWidth: '400px', fontFamily: 'inherit' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                <img
                    src="/logo.png"
                    alt="DataFre8"
                    style={{ height: '20px', width: 'auto' }}
                />
            </Box>
            <Typography variant="h4" fontWeight={600} textAlign="center" sx={{ mt: 0, mb: 1 }}>
                Sign in to Lantern
            </Typography>
            
            <Typography
                variant="body2"
                textAlign="center"
                sx={{ mb: 3, color: 'text.secondary', fontSize: '0.9rem' }}
            >
                Lantern is the interface for DataFre8
            </Typography>

            {signupComplete && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Your account has been created. Sign in with your email and password to continue.
                </Alert>
            )}

            {/* Google Login Button */}
            {/*
            <Button
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleLogin}
                sx={{
                    mt: 3,
                    mb: 3,
                    py: 1.5,
                    borderColor: '#ddd',
                    color: '#333',
                    '&:hover': {
                        borderColor: '#2D5EFF',
                        backgroundColor: '#f8f9ff'
                    }
                }}
            >
                Sign in with Google
            </Button>
            <Divider sx={{ my: 3 }}>or</Divider>
            */}

            {/* Email/Username Field */}
            <TextField
                type="text"
                label="Email or username"
                placeholder="you@example.com or username"
                fullWidth
                value={formData.username}
                onChange={handleInputChange('username')}
                sx={{ mb: 3 }}
            />

            {/* Password Field */}
            <TextField
                type={showPassword ? "text" : "password"}
                label="Password"
                fullWidth
                value={formData.password}
                onChange={handleInputChange('password')}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleTogglePassword} edge="end">
                                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 3 }}
            />

            {/* Terms */}
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                By signing in, you agree to our{' '}
                <Link href={termsUrl} target="_self" sx={{ color: '#2D5EFF', cursor: 'pointer' }}>
                    Terms of Use
                </Link> and{' '}
                <Link href={privacyUrl} target="_self" sx={{ color: '#2D5EFF', cursor: 'pointer' }}>
                    Privacy Policy
                </Link>.
            </Typography>

            {/* Sign In Button */}
            <Button
                variant="contained"
                fullWidth
                endIcon={<ArrowForwardIcon />}
                onClick={handleSignIn}
                disabled={!formData.username || !formData.password || loading}
                sx={{
                    py: 1.5,
                    backgroundColor: '#3B82F6',
                    '&:hover': {
                        backgroundColor: '#3B82F6',
                        filter: 'brightness(0.95)'
                    },
                    '&:disabled': {
                        backgroundColor: '#ccc'
                    }
                }}
            >
                {loading ? 'Signing in...' : 'Sign in'}
            </Button>

            {/* Forgot Password */}
            <Typography 
                variant="body2" 
                textAlign="center" 
                sx={{ 
                    mt: 2, 
                    cursor: 'pointer',
                    color: '#2D5EFF',
                    '&:hover': { textDecoration: 'underline' }
                }}
                onClick={handleForgotPassword}
            >
                Forgot your password?
            </Typography>

            {isOpenSignup ? (
            <Typography
                variant="body2"
                textAlign="center"
                sx={{ mt: 2, color: 'text.secondary' }}
            >
                New to DataFre8?{' '}
                <Link
                    href="/signup"
                    target="_self"
                    sx={{ color: '#2D5EFF', cursor: 'pointer', fontWeight: 600 }}
                    onClick={(event) => {
                        event.preventDefault();
                        navigate('/signup');
                    }}
                >
                    Sign up
                </Link>
            </Typography>
            ) : (
            <>
            <Typography
                variant="body2"
                textAlign="center"
                sx={{ mt: 2, color: 'text.secondary' }}
            >
                Have an invitation code?{' '}
                <Link
                    href="/signup"
                    target="_self"
                    sx={{ color: '#2D5EFF', cursor: 'pointer', fontWeight: 600 }}
                    onClick={(event) => {
                        event.preventDefault();
                        navigate('/signup');
                    }}
                >
                    Create your account
                </Link>
                .
            </Typography>

            {/* Request Access Link */}
            <Typography
                variant="body2"
                textAlign="center"
                sx={{ mt: 2, color: 'text.secondary' }}
            >
                Don’t have an account yet?
            </Typography>

            <Typography
                variant="body2"
                textAlign="center"
                sx={{ mt: 0.75, color: 'text.secondary', lineHeight: 1.6 }}
            >
                DataFre8 isn’t currently open for instant self-sign-up.{' '}
                <Link
                    href={requestAccessUrl}
                    target="_self"
                    sx={{ color: '#2D5EFF', cursor: 'pointer', fontWeight: 600 }}
                >
                    Request access
                </Link>{' '}
                to get started.
            </Typography>
            </>
            )}
        </Box>
    );



    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            backgroundColor: '#f7f9fb',
            fontFamily: 'inherit'
        }}>
            <Container maxWidth="lg">
                <Paper 
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: '20px',
                        overflow: 'hidden',
                        minHeight: '600px',
                        boxShadow: '0 18px 36px rgba(0, 0, 0, 0.04)',
                        border: '1px solid #e5e7eb'
                    }}
                >
                    {/* Left Side - Form */}
                    <Box sx={{
                        flex: 1,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'background.paper',
                        position: 'relative'
                    }}>
                        {/* Return to Home Button */}
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={handleReturnToHome}
                            sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                color: '#2D5EFF',
                                '&:hover': {
                                    backgroundColor: 'rgba(45, 94, 255, 0.08)'
                                }
                            }}
                        >
                            Return to Home
                        </Button>
                        
                        {/* Form Content */}
                        {renderSignInForm()}
                    </Box>

                    {/* Right Side - Background Image */}
                    <Box sx={{
                        flex: 1,
                        backgroundImage: `url(/lantern.png)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'saturate(0.9) contrast(0.95)',
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        textAlign: 'center',
                        color: '#fff',
                        p: 3,
                        position: 'relative'
                    }}>
                        {/* Background overlay to soften contrast */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255, 255, 255, 0.12)',
                            zIndex: 1
                        }} />
                    </Box>
                </Paper>
            </Container>

            {/* Error/Success Modal */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
                <DialogContent sx={{ 
                    fontFamily: 'inherit', 
                    color: modalStatus === 'success' ? '#1a962b' : '#d32f2f', 
                    textAlign: 'center', 
                    minWidth: 300 
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'inherit' }}>
                        {modalStatus === 'success' ? 'Success' : 'Error'}
                    </Typography>
                    <Typography sx={{ fontFamily: 'inherit' }}>{modalMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)} color="primary" sx={{ fontFamily: 'inherit' }}>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Login; 
